import { cn } from "@/lib/utils";
import Container from "@/components/Container";
import { getDefaultProofBarItems, type ProofBarItem } from "@/content/proofTrust";
import { useTranslation } from "@/i18n/useTranslation";

type ProofBarProps = {
  items?: ProofBarItem[];
  dark?: boolean;
  className?: string;
  ariaLabel?: string;
};

const ProofBar = ({ items, dark, className, ariaLabel }: ProofBarProps) => {
  const { locale } = useTranslation();
  const proofItems = items ?? getDefaultProofBarItems(locale);
  const fiveColumnLayout = proofItems.length >= 5;

  return (
    <section
      aria-label={ariaLabel ?? (locale === "fr" ? "Points de preuve" : "Proof points")}
      className={cn(
        "relative border-y",
        dark
          ? "border-[hsl(var(--sd-fg)/0.1)] bg-[hsl(var(--control-panel)/0.96)]"
          : "border-border/70 bg-card shadow-[0_1px_0_hsl(var(--foreground)/0.04)]",
        className,
      )}
    >
      <Container>
        <dl
          className={cn(
            "grid grid-cols-1 divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0",
            fiveColumnLayout ? "lg:grid-cols-5" : "lg:grid-cols-4",
            dark ? "divide-[hsl(var(--sd-fg)/0.08)]" : "divide-border/55",
          )}
        >
          {proofItems.map((item) => (
            <div key={item.code} className="group relative min-w-0 py-5 sm:px-6 first:sm:pl-0 last:sm:pr-0">
              <div
                className={cn(
                  "absolute left-0 top-5 hidden h-8 w-px sm:block",
                  dark ? "bg-primary/35" : "bg-primary/30",
                )}
                aria-hidden="true"
              />
              <dt
                className={cn(
                  "mb-2 flex items-center gap-2 font-mono text-[9.5px] tracking-[0.16em] uppercase",
                  dark ? "text-primary/70" : "text-primary/80",
                )}
              >
                <span>{item.code}</span>
                <span className={cn("h-px w-4", dark ? "bg-[hsl(var(--sd-fg)/0.16)]" : "bg-border/80")} aria-hidden="true" />
                <span>{item.label}</span>
              </dt>
              <dd className={cn("text-[13.5px] leading-[1.65]", dark ? "text-[hsl(var(--sd-fg)/0.74)]" : "text-foreground/76")}>
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </Container>
    </section>
  );
};

export default ProofBar;
